import { ref } from 'vue';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { validatePostalCode } from '@/utils/locationUtils';

export function usePostalCodeManager() {
  const postalCode = ref('');
  const loading = ref(false);
  const error = ref(null);
  const isValidPostalCode = ref(false);
  const suggestedAddress = ref('');
  const latitude = ref(null);
  const longitude = ref(null);
  const postalTown = ref('');
  const route = ref('');

  // Posta kodunu doğrula ama kaydetme
  const validatePostCode = async (code) => {
    if (!code || code.trim() === '') {
      error.value = 'Please enter a postal code';
      isValidPostalCode.value = false;
      return false;
    }

    loading.value = true;
    error.value = null;

    try {
      const result = await validatePostalCode(code);
      console.log('Postal code validation result:', result);

      if (result.isValid) {
        postalCode.value = code;
        latitude.value = result.latitude;
        longitude.value = result.longitude;
        suggestedAddress.value = result.formattedAddress;
        postalTown.value = result.postalTown || '';
        route.value = result.route || '';
        isValidPostalCode.value = true;
        return true;
      } else {
        error.value = result.error || 'Invalid postal code';
        isValidPostalCode.value = false;
        return false;
      }
    } catch (err) {
      console.error('Error validating postal code:', err);
      error.value = err.message || 'Error validating postal code';
      isValidPostalCode.value = false;
      return false;
    } finally {
      loading.value = false;
    }
  };

  // Sadece posta kodu ve koordinat bilgilerini kaydet
  const savePostalCodeData = async () => {
    const isClearing = !postalCode.value || postalCode.value.trim() === '';

    if (!isClearing && !isValidPostalCode.value) {
      error.value = 'Please validate postal code first';
      return false;
    }

    loading.value = true;
    error.value = null;

    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const userRef = doc(db, 'users', currentUser.uid);
      
      // Sadece konum bilgilerini güncelle
      await updateDoc(userRef, {
        postalCode: isClearing ? null : postalCode.value,
        latitude: isClearing ? null : latitude.value,
        longitude: isClearing ? null : longitude.value,
        postalTown: isClearing ? null : postalTown.value,
        route: isClearing ? null : route.value,
        // Diğer profil bilgilerini güncellemiyoruz
      });

      if (isClearing) {
        latitude.value = null;
        longitude.value = null;
        postalTown.value = '';
        route.value = '';
        suggestedAddress.value = '';
        isValidPostalCode.value = false;
      }

      console.log('Postal code data saved successfully');
      return true;
    } catch (err) {
      console.error('Error saving postal code data:', err);
      error.value = err.message || 'Error saving postal code data';
      return false;
    } finally {
      loading.value = false;
    }
  };

  // Mevcut posta kodu bilgilerini getir
  const loadPostalCodeData = async () => {
    loading.value = true;
    error.value = null;

    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        postalCode.value = userData.postalCode || '';
        latitude.value = userData.latitude || null;
        longitude.value = userData.longitude || null;
        postalTown.value = userData.postalTown || '';
        route.value = userData.route || '';
        
        // Eğer posta kodu varsa geçerli kabul edelim
        isValidPostalCode.value = !!userData.postalCode;
      }
    } catch (err) {
      console.error('Error loading postal code data:', err);
      error.value = err.message || 'Error loading postal code data';
    } finally {
      loading.value = false;
    }
  };

  return {
    postalCode,
    loading,
    error,
    isValidPostalCode,
    suggestedAddress,
    latitude,
    longitude,
    postalTown,
    route,
    validatePostCode,
    savePostalCodeData,
    loadPostalCodeData
  };
}